import * as THREE from 'three';
import { VEHICLES, stockColorOf } from '@/entities/vehicles';
import { ModelLoader, modelLoader } from '@/render/ModelLoader';
import { applyCarMaterials } from '@/entities/CarView';
import { attachModRig } from '@/entities/mods/ModRig';
import { surfaceFromObject } from '@/entities/mods/modSurface';
import type { CarLook } from '@/entities/carLook';
import { EnvironmentBuilder } from '@/render/Environment';
import { buildTrophy, type TrophyBuild } from '@/ui/title/trophy';
import { paintTitleBackdrop } from '@/ui/title/titleBackdrop';
import { CITIES } from '@/config/cities';
import { solveCupArc, bounceAt, fallY, type CupArc, type CupArcSpec } from '@/ui/celebration/podiumMath';
import { sfxCollect } from '@/audio/sfx';
import { subscribeViewport, type ViewportUnsubscribe } from '@/ui/viewport';

/**
 * TourPodiumClip.ts — the TOUR FINALE celebration cinematic (M94, owner spec).
 *
 * After the final tour race, the top three racers' REAL vehicles (their GLBs,
 * their equipped looks, name floating above each) push the green-and-gold
 * WORLD TOUR CUP globe-balls up ramps onto a three-height winner's podium,
 * shove them over the front edge, the balls bounce once on the ground and
 * land EXACTLY in each racer's trophy (gold / silver / bronze, the real
 * buildTrophy cup) waiting in front of their podium spot — completing the
 * full Dribble Racer cup. Then all cars light up a stationary BURNOUT (tyre
 * smoke, tail wag) while FIREWORKS go off behind the podium, a big congrats
 * message pops, and the overlay fades into the final tour standings.
 *
 * IntroClip pattern (M86): own renderer + letterboxed overlay, real assets,
 * one promise per play() that resolves 'ended' | 'skipped' | 'cancelled'
 * (cancelled = something else tore the flow down; the caller must NOT
 * navigate). Tap anywhere skips. Reduced motion never plays it (caller
 * skips straight to the summary). `?debug=1` exposes `__podiumClip` for
 * manual step() verification (rAF freezes in a backgrounded preview tab).
 */

export interface PodiumRacerSpec {
  /** Final championship place, 1..3. */
  place: number;
  name: string;
  /** The racer's identity (ball) colour — tints the name plate. */
  color: number;
  vehicleId: string;
  /** Equipped cosmetics (undefined = the vehicle's stock livery, M93 rule). */
  look?: CarLook;
  isLocal: boolean;
}

export interface PodiumPlayOpts {
  /** Fired once when the congrats message pops (the caller's fanfare moment). */
  onCongrats?: () => void;
}

export type PodiumClipResult = 'ended' | 'skipped' | 'cancelled';

// ---- Stage layout (world units; podium faces +Z, camera out at +Z) ---------
/** Lane x by place (1st centre, 2nd viewer-left, 3rd viewer-right). */
const LANE_X = [0, -6.8, 6.8];
/** Platform top heights by place. */
const LANE_H = [3.3, 2.45, 1.75];
const PLAT_W = 5.4; // platform width (x)
const PLAT_BACK = -4.6; // platform rear edge (ramp crest)
const PLAT_FRONT = 2.6; // the front edge the balls roll over
const RAMP_START = -20; // where each ramp leaves the ground
const CAR_START = -26; // cars stage here in the fog
const CAR_STOP = -1.7; // nose short of the edge after the climb
const PUSH_DIST = 1.15; // the deliberate push lunge
const BALL_R = 1.5; // = the trophy's seated-globe radius (buildTrophy)
const BALL_LEAD = 3.6; // ball rides this far ahead of the car centre
const TROPHY_Z = 10.5; // trophies wait here, on the ground in front (clear of
// the podium sightline — at 7.6 they occluded the platforms from the camera)

// ---- Timeline (per lane, from its own start; lanes cascade by place) -------
const START_DELAYS = [0.35, 0.95, 1.55];
const CLIMB_S = 3.0; // up the ramp to the stop
const PUSH_WAIT = 0.3; // a beat before the push
const PUSH_S = 0.32; // the lunge that tips the ball over
const G = 26; // gamey gravity (the title-stage feel)
const EDGE_VZ = 6.0; // forward speed the push gives the ball
const CUP_ARC_S = 0.68; // the bounce-to-cup arc duration (feel knob)
const SQUASH_T = 0.09; // ground-contact squash beat
const SETTLE_T = 0.6; // damped wobble once seated
/** The bounce arc ends this far ABOVE the cradle, then the ball sinks in —
 *  aiming straight at the seat grazed the cup's front rim (measured). */
const DROP_IN = 0.55;
const CELEBRATE_S = 5.6; // burnout + fireworks phase length
const CONGRATS_AT = 1.5; // congrats pops this far into the celebration
const FADE_S = 0.9; // overlay fade into the standings
const FAILSAFE_S = 26; // wall-clock kill switch

interface SmokePuff {
  mesh: THREE.Mesh;
  vel: THREE.Vector3;
  age: number;
  life: number;
}

interface FireworkSpark {
  mesh: THREE.Mesh;
  vel: THREE.Vector3;
}

interface Firework {
  sparks: FireworkSpark[];
  color: THREE.Color;
  age: number; // > life = idle
  life: number;
}

type BallPhase = 'drive' | 'flight' | 'seated';

interface Lane {
  spec: PodiumRacerSpec;
  x: number;
  h: number;
  start: number; // this lane's timeline offset
  car: THREE.Group;
  label: THREE.Sprite;
  ballHolder: THREE.Group;
  ballSpin: THREE.Group; // the globe inside (spins for the roll)
  trophy: TrophyBuild;
  cradle: THREE.Vector3; // world-space seat point
  arcSpec: CupArcSpec;
  arc: CupArc;
  phase: BallPhase;
  flightT: number;
  settleT: number;
  smoke: SmokePuff[];
  smokeClock: number;
  flash: THREE.Sprite;
  ring: THREE.Sprite;
  fxT: number;
}

const easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3);
const easeInOut = (t: number): number => t * t * (3 - 2 * t);
const clamp01 = (t: number): number => Math.min(1, Math.max(0, t));

/** Soft radial sprite texture (flash / shockwave / smoke), cached per look. */
function radialTexture(inner: string, mid: string, outer: string): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 128;
  c.height = 128;
  const g = c.getContext('2d')!;
  const grad = g.createRadialGradient(64, 64, 4, 64, 64, 62);
  grad.addColorStop(0, inner);
  grad.addColorStop(0.45, mid);
  grad.addColorStop(1, outer);
  g.fillStyle = grad;
  g.fillRect(0, 0, 128, 128);
  return new THREE.CanvasTexture(c);
}

/** Floating name plate above a car: the racer's name over a colour bar. */
function nameTexture(name: string, color: number): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 512;
  c.height = 128;
  const g = c.getContext('2d')!;
  const text = name.toUpperCase();
  g.font = 'italic 900 58px system-ui, sans-serif';
  const w = Math.min(470, g.measureText(text).width + 56);
  // Glass pill behind the name so it reads over any backdrop.
  g.fillStyle = 'rgba(6, 9, 18, 0.72)';
  g.beginPath();
  g.roundRect(256 - w / 2, 14, w, 76, 38);
  g.fill();
  g.strokeStyle = 'rgba(255, 255, 255, 0.22)';
  g.lineWidth = 3;
  g.stroke();
  g.fillStyle = '#f2f5fa';
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.fillText(text, 256, 54);
  // The racer's identity colour as the underline bar (the ball is the identity).
  g.fillStyle = `#${color.toString(16).padStart(6, '0')}`;
  g.beginPath();
  g.roundRect(256 - w / 2 + 18, 96, w - 36, 12, 6);
  g.fill();
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

/** The checkered strip across each platform's front edge (the finish echo). */
function checkerTexture(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 192;
  c.height = 32;
  const g = c.getContext('2d')!;
  const sq = 16;
  for (let y = 0; y < 2; y++)
    for (let x = 0; x < 12; x++) {
      g.fillStyle = (x + y) % 2 === 0 ? '#e8edf5' : '#10151f';
      g.fillRect(x * sq, y * sq, sq, sq);
    }
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

/** The big place numeral on each platform's front face. */
function numeralTexture(place: number): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 256;
  c.height = 256;
  const g = c.getContext('2d')!;
  g.fillStyle = '#0c101c';
  g.fillRect(0, 0, 256, 256);
  g.strokeStyle = 'rgba(226, 176, 74, 0.55)';
  g.lineWidth = 7;
  g.strokeRect(12, 12, 232, 232);
  g.font = 'italic 900 170px system-ui, sans-serif';
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.fillStyle = '#e9bc55';
  g.fillText(String(place), 128, 140);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

/** Re-tint the gold parts of a trophy silver or bronze. Green bands and the
 *  engraved plate keep the trophy-family look; only goldish metals shift.
 *  Runs again as the trophy's async gold car lands (tracked via `tinted`). */
function tintTrophy(
  root: THREE.Object3D,
  kind: 'silver' | 'bronze',
  tinted: WeakSet<THREE.Material>,
): void {
  const hsl = { h: 0, s: 0, l: 0 };
  root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const m of mats) {
      const std = m as THREE.MeshStandardMaterial;
      if (!std || !std.color || std.map || tinted.has(std)) continue;
      std.color.getHSL(hsl);
      // Goldish = warm hue + real saturation (skips the green bands + darks).
      if (hsl.h < 0.05 || hsl.h > 0.17 || hsl.s < 0.25) continue;
      if (kind === 'silver') std.color.setHSL(0.58, 0.06, Math.min(0.85, hsl.l * 1.08));
      else std.color.setHSL(0.05, 0.52, hsl.l * 0.82);
      tinted.add(std);
    }
  });
}

export class TourPodiumClip {
  private readonly root: HTMLDivElement;
  private readonly congrats: HTMLDivElement;
  private renderer: THREE.WebGLRenderer | null = null;
  private env: EnvironmentBuilder | null = null;
  private scene = new THREE.Scene();
  private camera = new THREE.PerspectiveCamera(42, 16 / 9, 0.1, 400);
  /** Per-play actors (lanes, platforms) live here — rebuilt every play(). */
  private stage = new THREE.Group();
  private stageDisposables: { dispose(): void }[] = [];
  private lanes: Lane[] = [];
  private smokeTex: THREE.CanvasTexture | null = null;
  private flashTex: THREE.CanvasTexture | null = null;
  private ringTex: THREE.CanvasTexture | null = null;
  private fireworks: Firework[] = [];
  private fwClock = 0;
  /** The shared soccer-globe factory (lazy Globe.ts chunk; null = plain gold ball). */
  private globeFactory: (() => THREE.Group) | null = null;
  private assetsReady: Promise<unknown> = Promise.resolve();
  private raf: number | null = null;
  private lastT = 0;
  private t = 0; // world clock
  private wallT = 0;
  private celebrateT = -1; // >= 0 once every ball is seated
  private congratsShown = false;
  private fadeStarted = false;
  private opts: PodiumPlayOpts = {};
  private resolvePlay: ((r: PodiumClipResult) => void) | null = null;
  private camPos = new THREE.Vector3();
  private camAim = new THREE.Vector3();
  private trophyTintClock = 0;
  private readonly tintedMats = new WeakSet<THREE.Material>();
  private unsubViewport: ViewportUnsubscribe | null = null;
  private readonly onResize = (): void => this.resize();

  constructor(parent: HTMLElement) {
    this.root = document.createElement('div');
    this.root.className = 'podium-clip';
    this.root.style.display = 'none';
    parent.appendChild(this.root);

    const barTop = document.createElement('div');
    barTop.className = 'podium-clip-bar top';
    const barBot = document.createElement('div');
    barBot.className = 'podium-clip-bar bottom';
    this.congrats = document.createElement('div');
    this.congrats.className = 'podium-congrats';
    const skip = document.createElement('button');
    skip.type = 'button';
    skip.className = 'podium-clip-skip';
    skip.textContent = 'TAP TO SKIP ›';
    this.root.append(barTop, barBot, this.congrats, skip);
    this.root.addEventListener('click', () => this.finish('skipped'));
    this.unsubViewport = subscribeViewport(this.onResize);
    // Dev QA handle (the M74 pattern) — manual step() from the console.
    if (new URLSearchParams(location.search).has('debug')) {
      (window as unknown as Record<string, unknown>).__podiumClip = this;
    }
  }

  /** Play the celebration for the given podium (1..3 racers, place order).
   *  Resolves 'ended' (played through), 'skipped' (user tap) or 'cancelled'
   *  (cancel() — the caller must not navigate). */
  play(podium: PodiumRacerSpec[], opts: PodiumPlayOpts = {}): Promise<PodiumClipResult> {
    this.cancel(); // a stray previous run can never leak its resolve
    this.opts = opts;
    this.ensureScene();
    this.buildStage(podium.slice(0, 3));
    this.root.style.display = '';
    this.root.classList.remove('out');
    this.congrats.classList.remove('on');
    this.resetClocks();
    this.resize();
    this.renderer?.render(this.scene, this.camera); // never a blank first frame
    return new Promise((resolve) => {
      this.resolvePlay = resolve;
      const begin = (): void => {
        if (!this.resolvePlay || this.raf !== null) return; // skipped while loading
        this.resetClocks();
        this.lastT = performance.now();
        const tick = (): void => {
          this.raf = requestAnimationFrame(tick);
          const now = performance.now();
          const dt = Math.min(0.1, (now - this.lastT) / 1000);
          this.lastT = now;
          this.step(dt);
        };
        this.raf = requestAnimationFrame(tick);
      };
      // Wait for the car GLBs + globe chunk (capped — a slow network can only
      // delay the clip, never stall the finale). The M86 lesson: starting the
      // run before the cars exist shows lone rolling balls.
      void Promise.race([this.assetsReady, new Promise((r) => setTimeout(r, 2500))]).then(begin);
    });
  }

  /** External teardown (netClosed, leaving online, dispose). The play promise
   *  resolves 'cancelled' so the caller skips its follow-up navigation. */
  cancel(): void {
    this.finish('cancelled');
  }

  /** One step of REAL seconds (public-ish for ?debug=1 manual stepping). */
  step(dt: number): void {
    this.t += dt;
    this.wallT += dt;

    let allSeated = this.lanes.length > 0;
    for (const lane of this.lanes) {
      this.stepLane(lane, dt);
      if (lane.phase !== 'seated') allSeated = false;
    }
    if (allSeated && this.celebrateT < 0) this.celebrateT = 0;

    // ---- The celebration: burnouts + fireworks + the congrats pop ----------
    if (this.celebrateT >= 0) {
      this.celebrateT += dt;
      for (const lane of this.lanes) this.stepBurnout(lane, dt);
      this.stepFireworks(dt, true);
      if (!this.congratsShown && this.celebrateT >= CONGRATS_AT) {
        this.congratsShown = true;
        this.congrats.classList.add('on');
        this.opts.onCongrats?.();
      }
      if (this.celebrateT >= CELEBRATE_S && !this.fadeStarted) {
        this.fadeStarted = true;
        this.root.classList.add('out');
      }
      if (this.celebrateT >= CELEBRATE_S + FADE_S) {
        this.finish('ended');
        return;
      }
    } else {
      this.stepFireworks(dt, false); // decay any pool residue only
    }

    // Trophy tint upkeep: the async gold statuette car lands whenever its GLB
    // does — re-tint silver/bronze trophies until the scene settles (cheap).
    this.trophyTintClock += dt;
    if (this.trophyTintClock > 0.5 && this.t < 6) {
      this.trophyTintClock = 0;
      for (const lane of this.lanes) {
        if (lane.spec.place === 2) tintTrophy(lane.trophy.group, 'silver', this.tintedMats);
        if (lane.spec.place === 3) tintTrophy(lane.trophy.group, 'bronze', this.tintedMats);
      }
    }

    this.updateCamera(dt);
    this.renderer?.render(this.scene, this.camera);
    if (this.wallT >= FAILSAFE_S) this.finish('ended');
  }

  // ---- Per-lane scripting ---------------------------------------------------

  /** Ramp surface height under z for a lane of platform height `h`. */
  private rampY(z: number, h: number): number {
    if (z <= RAMP_START) return 0;
    if (z >= PLAT_BACK) return h;
    return (h * (z - RAMP_START)) / (PLAT_BACK - RAMP_START);
  }

  /** The car's scripted z at lane-local time `lt`: climb, beat, push, settle. */
  private carZ(lt: number): number {
    if (lt <= 0) return CAR_START;
    if (lt < CLIMB_S) return CAR_START + (CAR_STOP - CAR_START) * easeOutCubic(lt / CLIMB_S);
    const pt = lt - CLIMB_S - PUSH_WAIT;
    if (pt < 0) return CAR_STOP;
    if (pt < PUSH_S) return CAR_STOP + PUSH_DIST * easeOutCubic(pt / PUSH_S);
    const st = clamp01((pt - PUSH_S) / 0.55);
    return CAR_STOP + PUSH_DIST - 0.5 * easeInOut(st); // roll back off the edge
  }

  private stepLane(lane: Lane, dt: number): void {
    const lt = this.t - lane.start;

    // --- The car (always scripted; the burnout phase adds wag on top). ------
    const cz = this.carZ(lt);
    // Pitch follows the ramp under the wheelbase (nose up on the climb).
    const noseY = this.rampY(cz + 1.9, lane.h);
    const tailY = this.rampY(cz - 1.9, lane.h);
    lane.car.position.set(lane.x, (noseY + tailY) / 2, cz);
    if (this.celebrateT < 0) {
      lane.car.rotation.set(-Math.atan2(noseY - tailY, 3.8), 0, 0);
    }

    // Name plate rides above the car, gently bobbing.
    lane.label.position.set(
      lane.x,
      lane.car.position.y + 3.4 + Math.sin(this.t * 1.7 + lane.x) * 0.12,
      cz,
    );

    // --- The ball -----------------------------------------------------------
    if (lane.phase === 'drive') {
      const bz = Math.min(this.carZ(Math.max(0, lt)) + BALL_LEAD, PLAT_FRONT + 0.4);
      const by = this.rampY(bz, lane.h) + BALL_R;
      const prevZ = lane.ballHolder.position.z;
      lane.ballHolder.position.set(lane.x, by, bz);
      lane.ballHolder.scale.setScalar(1);
      if (dt > 0) this.spinBall(lane, (bz - prevZ) / Math.max(1e-4, dt), dt);
      if (bz >= PLAT_FRONT + 0.35) {
        lane.phase = 'flight';
        lane.flightT = 0;
      }
      return;
    }

    if (lane.phase === 'flight') {
      lane.flightT += dt;
      const ft = lane.flightT;
      const spec = lane.arcSpec;
      const arc = lane.arc;
      if (ft < arc.t1) {
        // Off the edge: free fall, still travelling forward.
        lane.ballHolder.position.set(lane.x, fallY(spec, ft), spec.edgeZ + spec.vz0 * ft);
        lane.ballHolder.scale.setScalar(1);
        this.spinBall(lane, spec.vz0, dt);
      } else if (ft < arc.t1 + SQUASH_T) {
        // Ground contact: soccer squash (centre dips as it compresses).
        const s = (ft - arc.t1) / SQUASH_T;
        const sq = 1 - 0.24 * Math.sin(Math.PI * s);
        lane.ballHolder.position.set(lane.x, BALL_R * sq, arc.z1);
        lane.ballHolder.scale.set(1 + (1 - sq) * 0.7, sq, 1 + (1 - sq) * 0.7);
      } else if (ft < arc.t1 + SQUASH_T + arc.t2) {
        const bt = ft - arc.t1 - SQUASH_T;
        const p = bounceAt(spec, arc, bt);
        lane.ballHolder.position.set(lane.x, p.y, p.z);
        lane.ballHolder.scale.setScalar(1);
        this.spinBall(lane, arc.vz2, dt);
      } else {
        lane.phase = 'seated';
        lane.settleT = 0;
        lane.fxT = 0; // landing flash + shockwave
        sfxCollect();
      }
      return;
    }

    // Seated: sink the drop-in overshoot, then a damped settle wobble.
    lane.settleT += dt;
    lane.ballHolder.position.copy(lane.cradle);
    lane.ballHolder.position.y += DROP_IN * Math.max(0, 1 - lane.settleT / 0.14);
    if (lane.settleT < SETTLE_T) {
      const s = lane.settleT / SETTLE_T;
      const wob = Math.exp(-4.2 * s) * Math.sin(s * 18);
      lane.ballHolder.scale.set(1 - wob * 0.06, 1 + wob * 0.1, 1 - wob * 0.06);
    } else {
      lane.ballHolder.scale.setScalar(1);
      lane.ballSpin.rotation.y += dt * 0.5; // the trophy globe's slow idle spin
    }
    // Landing FX decay.
    lane.fxT += dt;
    const s = lane.fxT;
    const flashMat = lane.flash.material as THREE.SpriteMaterial;
    flashMat.opacity = Math.max(0, 1 - s / 0.4);
    lane.flash.scale.setScalar(1 + s * 14);
    const ringMat = lane.ring.material as THREE.SpriteMaterial;
    ringMat.opacity = Math.max(0, 0.85 - s / 0.65);
    const k = 1 + s * 12;
    lane.ring.scale.set(k, k, 1);
  }

  private spinBall(lane: Lane, vz: number, dt: number): void {
    lane.ballSpin.rotateOnWorldAxis(new THREE.Vector3(1, 0, 0), (vz * dt) / BALL_R);
  }

  // ---- Burnout + fireworks --------------------------------------------------

  private stepBurnout(lane: Lane, dt: number): void {
    const t = this.celebrateT;
    // Stationary burnout: the tail wags, the body shudders on its suspension.
    lane.car.rotation.y = Math.sin(t * 3.1 + lane.x) * 0.16;
    lane.car.rotation.z = Math.sin(t * 31 + lane.x * 2) * 0.015;
    lane.car.rotation.x = -0.01 + Math.sin(t * 27 + lane.x) * 0.012;

    // Tyre smoke pours off the rear wheels.
    lane.smokeClock -= dt;
    if (lane.smokeClock <= 0) {
      lane.smokeClock = 0.045;
      const puff = lane.smoke.find((p) => p.age >= p.life);
      if (puff) {
        const side = Math.random() < 0.5 ? -1 : 1;
        puff.age = 0;
        puff.life = 1.1 + Math.random() * 0.5;
        puff.mesh.visible = true;
        puff.mesh.position.set(
          lane.x + side * 1.1,
          lane.car.position.y + 0.35,
          lane.car.position.z - 1.6,
        );
        puff.vel.set(side * (0.8 + Math.random() * 1.2), 2.0 + Math.random() * 1.4, -2.6 - Math.random() * 1.8);
        puff.mesh.scale.setScalar(0.6);
      }
    }
    for (const p of lane.smoke) {
      if (p.age >= p.life) {
        p.mesh.visible = false;
        continue;
      }
      p.age += dt;
      const u = p.age / p.life;
      p.mesh.position.addScaledVector(p.vel, dt);
      p.vel.multiplyScalar(1 - dt * 1.6);
      p.mesh.scale.setScalar(0.6 + u * 3.3);
      (p.mesh.material as THREE.MeshBasicMaterial).opacity = 0.68 * (1 - u);
      p.mesh.quaternion.copy(this.camera.quaternion);
    }
  }

  private stepFireworks(dt: number, launching: boolean): void {
    if (launching) {
      this.fwClock -= dt;
      if (this.fwClock <= 0 && this.celebrateT < CELEBRATE_S - 1.2) {
        this.fwClock = 0.5 + Math.random() * 0.25;
        const fw = this.fireworks.find((f) => f.age >= f.life);
        if (fw) this.igniteFirework(fw);
      }
    }
    for (const fw of this.fireworks) {
      if (fw.age >= fw.life) continue;
      fw.age += dt;
      const u = fw.age / fw.life;
      for (const s of fw.sparks) {
        s.vel.y -= 9.5 * dt;
        s.vel.multiplyScalar(1 - dt * 1.1);
        s.mesh.position.addScaledVector(s.vel, dt);
        const m = s.mesh.material as THREE.MeshBasicMaterial;
        m.opacity = Math.max(0, 1 - u) * (0.75 + 0.25 * Math.sin(fw.age * 31 + s.mesh.id));
        s.mesh.visible = m.opacity > 0.02;
        s.mesh.quaternion.copy(this.camera.quaternion);
      }
      if (fw.age >= fw.life) {
        for (const s of fw.sparks) s.mesh.visible = false;
      }
    }
  }

  private igniteFirework(fw: Firework): void {
    const palette = [0xffd34a, 0xff5a4c, 0x6ef0b9, 0x8ab4ff, 0xffffff, 0xf2b21c];
    fw.color.set(palette[Math.floor(Math.random() * palette.length)]);
    fw.age = 0;
    fw.life = 1.7;
    const at = new THREE.Vector3((Math.random() - 0.5) * 34, 9 + Math.random() * 7, -17);
    for (const s of fw.sparks) {
      const dir = new THREE.Vector3(
        Math.random() * 2 - 1,
        Math.random() * 2 - 1,
        Math.random() * 2 - 1,
      ).normalize();
      s.mesh.position.copy(at);
      s.vel.copy(dir).multiplyScalar(6.5 + Math.random() * 5.5);
      s.mesh.visible = true;
      (s.mesh.material as THREE.MeshBasicMaterial).color.set(fw.color);
      (s.mesh.material as THREE.MeshBasicMaterial).opacity = 1;
    }
  }

  // ---- Camera ---------------------------------------------------------------

  private updateCamera(dt: number): void {
    // A narrow (portrait) viewport pulls the camera back so the full podium
    // stays in frame; the letterbox bars keep it reading as cinema.
    const k = Math.min(2.3, Math.max(1, 1.7 / this.camera.aspect));
    let target: THREE.Vector3;
    let aim: THREE.Vector3;
    if (this.celebrateT >= 0) {
      // Wide celebration frame: podium, trophies, smoke, fireworks.
      target = new THREE.Vector3(Math.sin(this.celebrateT * 0.22) * 3.5, 10 * k, 36 * k);
      aim = new THREE.Vector3(0, 3.4, -1);
    } else if (this.t > CLIMB_S + 0.4) {
      // Push-in for the shoves + bounces: HIGH and closer, looking down into
      // the cup mouths (a level camera hides the landing behind the trophy —
      // measured).
      target = new THREE.Vector3(5, 12.5 * k, 26.5 * k);
      aim = new THREE.Vector3(0, 2.0, 4);
    } else {
      // The approach: high front three-quarter, watching the climbs over the
      // waiting trophies (a low camera hides the podium behind them — measured).
      target = new THREE.Vector3(14, 9.5 * k, 32 * k);
      aim = new THREE.Vector3(-1, 2.8, -3);
    }
    const ease = Math.min(1, dt * 1.4);
    this.camPos.lerp(target, ease);
    this.camAim.lerp(aim, ease);
    this.camera.position.copy(this.camPos);
    this.camera.lookAt(this.camAim);
  }

  // ---- DOM: the congrats message -------------------------------------------

  private buildCongrats(podium: PodiumRacerSpec[]): void {
    this.congrats.replaceChildren();
    const kicker = document.createElement('div');
    kicker.className = 'podium-congrats-kicker';
    kicker.textContent = 'WORLD TOUR CUP';
    const title = document.createElement('div');
    title.className = 'podium-congrats-title';
    const local = podium.find((p) => p.isLocal);
    title.textContent =
      local?.place === 1
        ? 'YOU ARE THE WORLD CHAMPION!'
        : local
          ? 'PODIUM FINISH! CONGRATULATIONS!'
          : `${podium[0]?.name.toUpperCase() ?? 'THE CHAMPION'} TAKES THE TITLE!`;
    const rows = document.createElement('div');
    rows.className = 'podium-congrats-rows';
    const placeWord = ['CHAMPION', '2ND PLACE', '3RD PLACE'];
    const medal = ['gold', 'silver', 'bronze'];
    for (const p of podium) {
      const row = document.createElement('div');
      row.className = `podium-congrats-row medal-${medal[p.place - 1] ?? 'gold'}${p.isLocal ? ' me' : ''}`;
      const chip = document.createElement('span');
      chip.className = 'podium-congrats-place';
      chip.textContent = placeWord[p.place - 1] ?? `${p.place}TH`;
      const name = document.createElement('span');
      name.className = 'podium-congrats-name';
      name.textContent = p.name.toUpperCase();
      row.append(chip, name);
      rows.appendChild(row);
    }
    this.congrats.append(kicker, title, rows);
  }

  // ---- Scene construction ---------------------------------------------------

  private ensureScene(): void {
    if (this.renderer) return;
    // The stadium-night painting (the title screen's language) backs the whole
    // clip; the 3D stage renders transparent over it (the TitleStage trick).
    const bg = paintTitleBackdrop();
    bg.className = 'podium-clip-bg';
    this.root.prepend(bg);
    const grade = document.createElement('div');
    grade.className = 'podium-clip-grade';
    bg.after(grade);

    const r = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    r.setClearColor(0x000000, 0);
    r.toneMapping = THREE.ACESFilmicToneMapping;
    r.outputColorSpace = THREE.SRGBColorSpace;
    r.domElement.className = 'podium-clip-canvas';
    grade.after(r.domElement); // bg → grade → 3D canvas (the title's layering)
    this.renderer = r;

    this.env = new EnvironmentBuilder(r);
    this.scene.environment = this.env.generate({
      skyTop: 0x0a1226,
      skyHorizon: 0x33280f, // warm stadium glow low on the horizon — gold lives in it
      sun: 0xffe9b0,
      sunIntensity: 2.0,
      sunDir: new THREE.Vector3(-0.25, 0.85, 0.45).normalize(),
      ground: 0x06080e,
    });

    this.scene.add(new THREE.HemisphereLight(0x9fb4dd, 0x0b0e16, 0.95));
    const key = new THREE.DirectionalLight(0xfff2dc, 2.0);
    key.position.set(-10, 16, 12);
    this.scene.add(key);
    // Warm floodlight pool over the podium — the hero moment is the bright spot.
    const flood = new THREE.PointLight(0xffe9b0, 420, 90, 1.8);
    flood.position.set(0, 14, 4);
    this.scene.add(flood);
    // A cool rim from behind so the cars' silhouettes pop off the backdrop.
    const rim = new THREE.DirectionalLight(0x7ea0ff, 0.5);
    rim.position.set(6, 8, -18);
    this.scene.add(rim);
    // Fog folds the floor's far edge into the night — without it the plane's
    // rim reads as a hard horizon line and the env washes it blue (measured).
    this.scene.fog = new THREE.Fog(0x05070f, 55, 150);

    // Matte night floor — the balls bounce ON this. Kept near-black: any
    // sheen mirrored the environment as a big blue wash (measured).
    const floor = new THREE.Mesh(
      new THREE.PlaneGeometry(240, 160),
      new THREE.MeshStandardMaterial({
        color: 0x0a0d16,
        roughness: 0.92,
        metalness: 0.05,
        envMapIntensity: 0.18,
      }),
    );
    floor.rotation.x = -Math.PI / 2;
    this.scene.add(floor);
    // A soft gold pool where the trophies stand.
    const pool = new THREE.Mesh(
      new THREE.CircleGeometry(16, 40),
      new THREE.MeshBasicMaterial({
        map: radialTexture('rgba(255,211,74,0.30)', 'rgba(255,180,60,0.10)', 'rgba(0,0,0,0)'),
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      }),
    );
    pool.rotation.x = -Math.PI / 2;
    pool.position.set(0, 0.02, 4);
    this.scene.add(pool);

    this.scene.add(this.stage);

    // Shared FX textures + the fireworks pool (idle until the celebration).
    this.smokeTex = radialTexture('rgba(235,238,245,0.85)', 'rgba(200,205,215,0.35)', 'rgba(180,185,195,0)');
    this.flashTex = radialTexture('rgba(255,255,255,1)', 'rgba(255,225,140,0.55)', 'rgba(255,211,74,0)');
    this.ringTex = ringTexture();
    for (let b = 0; b < 5; b++) {
      const sparks: FireworkSpark[] = [];
      for (let i = 0; i < 34; i++) {
        const mesh = new THREE.Mesh(
          new THREE.PlaneGeometry(0.34, 0.34),
          new THREE.MeshBasicMaterial({
            map: this.flashTex,
            transparent: true,
            opacity: 0,
            depthWrite: false,
            blending: THREE.AdditiveBlending,
          }),
        );
        mesh.visible = false;
        this.scene.add(mesh);
        sparks.push({ mesh, vel: new THREE.Vector3() });
      }
      this.fireworks.push({ sparks, color: new THREE.Color(), age: 99, life: 1 });
    }

    // The soccer-globe factory (its own chunk — already cached after any tour
    // flight). Failure falls back to a plain gold match ball: never blocks.
    const globeReady = import('@/world/Globe')
      .then(({ buildEarth, GLOBE_R }) => {
        this.globeFactory = () => {
          const earth = buildEarth(CITIES, 'ball');
          earth.group.scale.setScalar(BALL_R / GLOBE_R);
          return earth.group;
        };
      })
      .catch(() => {
        this.globeFactory = null;
      });
    this.assetsReady = globeReady;
  }

  /** Build the per-play actors: platforms, ramps, trophies, cars, balls. */
  private buildStage(podium: PodiumRacerSpec[]): void {
    // Tear down the previous play's actors.
    for (const d of this.stageDisposables) d.dispose();
    this.stageDisposables = [];
    this.stage.clear();
    this.lanes = [];
    this.buildCongrats(podium);

    const geoms: THREE.BufferGeometry[] = [];
    const mats: THREE.Material[] = [];
    this.stageDisposables.push({
      dispose: () => {
        for (const g of geoms) g.dispose();
        for (const m of mats) m.dispose();
      },
    });
    const track = <T extends THREE.BufferGeometry>(g: T): T => {
      geoms.push(g);
      return g;
    };
    const trackM = <T extends THREE.Material>(m: T): T => {
      mats.push(m);
      return m;
    };

    const blockMat = trackM(
      new THREE.MeshStandardMaterial({ color: 0x131722, roughness: 0.35, metalness: 0.5, envMapIntensity: 0.9 }),
    );
    const rampMat = trackM(
      new THREE.MeshStandardMaterial({ color: 0x171b27, roughness: 0.6, metalness: 0.3 }),
    );
    const goldCap = trackM(
      new THREE.MeshStandardMaterial({
        color: 0x1a1405,
        emissive: 0xffd34a,
        emissiveIntensity: 1.9,
        roughness: 0.5,
      }),
    );
    const checkerMat = trackM(
      new THREE.MeshBasicMaterial({ map: checkerTexture() }),
    );

    for (const spec of podium) {
      const idx = spec.place - 1;
      const x = LANE_X[idx] ?? 0;
      const h = LANE_H[idx] ?? 2;

      // Platform block + gold front lip + checkered edge strip + numeral.
      const block = new THREE.Mesh(
        track(new THREE.BoxGeometry(PLAT_W, h, PLAT_FRONT - PLAT_BACK)),
        blockMat,
      );
      block.position.set(x, h / 2, (PLAT_FRONT + PLAT_BACK) / 2);
      this.stage.add(block);
      const lip = new THREE.Mesh(track(new THREE.BoxGeometry(PLAT_W, 0.1, 0.16)), goldCap);
      lip.position.set(x, h + 0.03, PLAT_FRONT - 0.05);
      this.stage.add(lip);
      const checker = new THREE.Mesh(track(new THREE.PlaneGeometry(PLAT_W, 0.85)), checkerMat);
      checker.rotation.x = -Math.PI / 2;
      checker.position.set(x, h + 0.012, PLAT_FRONT - 0.55);
      this.stage.add(checker);
      const numeral = new THREE.Mesh(
        track(new THREE.PlaneGeometry(2.1, 2.1)),
        trackM(new THREE.MeshBasicMaterial({ map: numeralTexture(spec.place) })),
      );
      numeral.position.set(x, Math.max(1.1, h * 0.52), PLAT_FRONT + 0.02);
      this.stage.add(numeral);

      // Ramp: an inclined slab whose top surface IS the drive line.
      const rampLen = PLAT_BACK - RAMP_START;
      const slopeLen = Math.hypot(rampLen, h);
      const slope = Math.atan2(h, rampLen);
      const ramp = new THREE.Mesh(track(new THREE.BoxGeometry(PLAT_W, 0.5, slopeLen)), rampMat);
      ramp.rotation.x = slope;
      const mid = new THREE.Vector3(x, h / 2, (RAMP_START + PLAT_BACK) / 2);
      const normal = new THREE.Vector3(0, Math.cos(slope), Math.sin(slope));
      ramp.position.copy(mid).addScaledVector(normal, -0.25);
      this.stage.add(ramp);
      // Gold edge strips up the ramp sides (the course-wall glow language).
      for (const sx of [-1, 1]) {
        const strip = new THREE.Mesh(track(new THREE.BoxGeometry(0.14, 0.1, slopeLen)), goldCap);
        strip.rotation.x = slope;
        strip.position.set(x + (sx * PLAT_W) / 2, h / 2 + 0.06, (RAMP_START + PLAT_BACK) / 2);
        this.stage.add(strip);
      }

      // The trophy waiting on the ground in front (gold / silver / bronze).
      const trophy = buildTrophy();
      trophy.group.position.set(x, 0, TROPHY_Z);
      this.stage.add(trophy.group);
      this.stageDisposables.push(trophy);
      if (spec.place === 2) tintTrophy(trophy.group, 'silver', this.tintedMats);
      if (spec.place === 3) tintTrophy(trophy.group, 'bronze', this.tintedMats);
      const cradle = trophy.cradle.clone().add(trophy.group.position);

      // The car (real GLB, real look) + its beauty light + name plate.
      const car = new THREE.Group();
      this.stage.add(car);
      const beauty = new THREE.PointLight(0xffe6c4, 320, 40, 1.9);
      beauty.position.set(0, 5.5, 5);
      car.add(beauty);
      const def = VEHICLES[spec.vehicleId] ?? VEHICLES.sports;
      const carReady = modelLoader
        .load(def.url)
        .then((g) => {
          const fitted = ModelLoader.fit(g, def.length, def.forwardIsZ);
          applyCarMaterials(fitted, stockColorOf(def.id), spec.look, def.id);
          car.add(fitted);
          attachModRig(car, spec.look, def.id, stockColorOf(def.id), surfaceFromObject(fitted, car)); // M128
        })
        .catch(() => {
          const body = new THREE.Mesh(
            track(new THREE.BoxGeometry(1.9, 0.9, 4)),
            trackM(
              new THREE.MeshStandardMaterial({
                color: stockColorOf(def.id),
                roughness: 0.35,
                metalness: 0.5,
              }),
            ),
          );
          body.position.y = 0.75;
          car.add(body);
        });
      this.assetsReady = Promise.all([this.assetsReady, carReady]);

      const labelTex = nameTexture(spec.name, spec.color);
      const label = new THREE.Sprite(
        trackM(new THREE.SpriteMaterial({ map: labelTex, transparent: true, depthTest: false })),
      );
      label.scale.set(4.6, 1.15, 1);
      this.stage.add(label);
      this.stageDisposables.push({ dispose: () => labelTex.dispose() });

      // The globe-ball (or the gold fallback) inside spin + squash holders.
      const ballHolder = new THREE.Group();
      const ballSpin = new THREE.Group();
      ballHolder.add(ballSpin);
      const attachGlobe = (): void => {
        if (this.globeFactory) ballSpin.add(this.globeFactory());
        else {
          const ball = new THREE.Mesh(
            track(new THREE.SphereGeometry(BALL_R, 32, 24)),
            trackM(
              new THREE.MeshStandardMaterial({
                color: 0xe2b04a,
                metalness: 1,
                roughness: 0.28,
                envMapIntensity: 1.6,
              }),
            ),
          );
          ballSpin.add(ball);
        }
      };
      if (this.globeFactory) attachGlobe();
      else void this.assetsReady.then(attachGlobe);
      this.stage.add(ballHolder);

      // Landing FX sprites at the cradle.
      const flash = new THREE.Sprite(
        trackM(
          new THREE.SpriteMaterial({
            map: this.flashTex,
            transparent: true,
            opacity: 0,
            depthTest: false,
            blending: THREE.AdditiveBlending,
          }),
        ),
      );
      flash.position.copy(cradle);
      this.stage.add(flash);
      const ring = new THREE.Sprite(
        trackM(
          new THREE.SpriteMaterial({
            map: this.ringTex,
            transparent: true,
            opacity: 0,
            depthTest: false,
            blending: THREE.AdditiveBlending,
          }),
        ),
      );
      ring.position.copy(cradle);
      this.stage.add(ring);

      // Smoke pool for the burnout.
      const smoke: SmokePuff[] = [];
      for (let i = 0; i < 22; i++) {
        const mesh = new THREE.Mesh(
          track(new THREE.PlaneGeometry(1.5, 1.5)),
          trackM(
            new THREE.MeshBasicMaterial({
              map: this.smokeTex,
              transparent: true,
              opacity: 0,
              depthWrite: false,
            }),
          ),
        );
        mesh.visible = false;
        this.scene.add(mesh);
        smoke.push({ mesh, vel: new THREE.Vector3(), age: 9, life: 1 });
        this.stageDisposables.push({ dispose: () => this.scene.remove(mesh) });
      }

      const arcSpec: CupArcSpec = {
        h,
        ballR: BALL_R,
        edgeZ: PLAT_FRONT + 0.35,
        vz0: EDGE_VZ,
        cupZ: cradle.z,
        cupY: cradle.y + DROP_IN, // arc ends above the seat, then sinks in
        g: G,
        t2: CUP_ARC_S,
      };
      this.lanes.push({
        spec,
        x,
        h,
        start: START_DELAYS[idx] ?? 0,
        car,
        label,
        ballHolder,
        ballSpin,
        trophy,
        cradle,
        arcSpec,
        arc: solveCupArc(arcSpec),
        phase: 'drive',
        flightT: 0,
        settleT: 0,
        smoke,
        smokeClock: 0,
        flash,
        ring,
        fxT: 99,
      });
    }
  }

  private resetClocks(): void {
    this.t = 0;
    this.wallT = 0;
    this.celebrateT = -1;
    this.congratsShown = false;
    this.fadeStarted = false;
    this.fwClock = 0.2;
    this.camPos.set(9, 7, 30);
    this.camAim.set(0, 2.6, -4);
    this.trophyTintClock = 0;
    for (const lane of this.lanes) {
      lane.phase = 'drive';
      lane.flightT = 0;
      lane.settleT = 0;
      lane.fxT = 99;
      lane.smokeClock = 0;
      lane.car.rotation.set(0, 0, 0);
      this.stepLane(lane, 0);
    }
  }

  private finish(result: PodiumClipResult): void {
    if (!this.resolvePlay) return;
    const resolve = this.resolvePlay;
    this.resolvePlay = null;
    if (this.raf !== null) {
      cancelAnimationFrame(this.raf);
      this.raf = null;
    }
    this.root.style.display = 'none';
    this.root.classList.remove('out');
    this.congrats.classList.remove('on');
    resolve(result);
  }

  private resize(): void {
    if (!this.renderer || this.root.style.display === 'none') return;
    const w = Math.max(1, this.root.clientWidth);
    const h = Math.max(1, this.root.clientHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(w, h);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  dispose(): void {
    this.finish('cancelled');
    this.unsubViewport?.();
    for (const d of this.stageDisposables) d.dispose();
    this.env?.dispose();
    this.scene.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (mesh.geometry) mesh.geometry.dispose();
      const mat = mesh.material as THREE.Material | THREE.Material[] | undefined;
      if (Array.isArray(mat)) for (const m of mat) m.dispose();
      else if (mat) mat.dispose();
    });
    this.renderer?.dispose();
    this.root.remove();
  }
}

/** A crisp expanding ring (the landing shockwave). */
function ringTexture(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 128;
  c.height = 128;
  const g = c.getContext('2d')!;
  g.strokeStyle = 'rgba(255, 223, 128, 0.9)';
  g.lineWidth = 7;
  g.beginPath();
  g.arc(64, 64, 52, 0, Math.PI * 2);
  g.stroke();
  g.strokeStyle = 'rgba(255, 255, 255, 0.5)';
  g.lineWidth = 3;
  g.beginPath();
  g.arc(64, 64, 44, 0, Math.PI * 2);
  g.stroke();
  return new THREE.CanvasTexture(c);
}
